/**
 * NewCalculatorDialog.tsx — create a brand-new config-backed calculator via
 * POST /configs. New calcs are configBacked=true from creation (no legacy oracle).
 */

import { useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import { calculatorBuilderApi, type StudioConfig } from '../../../api/calculatorBuilder';
import { field, btn, btnAccent, label } from './ui';

interface Props {
  onCreated: (config: StudioConfig) => void;
  onClose: () => void;
}

function slugify(s: string): string {
  return s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
}

export default function NewCalculatorDialog({ onCreated, onClose }: Props) {
  const [title, setTitle] = useState('');
  const [key, setKey] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const effectiveKey = key || slugify(title);

  const create = async () => {
    setBusy(true);
    setErr(null);
    try {
      const { config } = await calculatorBuilderApi.createConfig({
        category: effectiveKey,
        key: effectiveKey,
        title: title.trim(),
        description: description.trim() || undefined,
        inputs: { fields: [] },
      });
      onCreated(config);
    } catch (e: any) {
      setErr(e?.response?.data?.message || 'Could not create the calculator.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.35)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 50,
      }}
      onClick={onClose}
    >
      <div
        style={{ background: 'var(--card, #fff)', borderRadius: 12, width: 440, maxWidth: '92vw', padding: 20 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 14 }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--ink-1, #1f2937)' }}>New calculator</div>
          <button type="button" style={{ ...btn, marginLeft: 'auto', height: 28, padding: '0 8px' }} onClick={onClose}>
            <X size={14} />
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <span style={label}>Title</span>
            <input style={{ ...field, width: '100%' }} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Greywater Reuse Sizer" autoFocus />
          </div>
          <div>
            <span style={label}>Key (auto from title)</span>
            <input
              style={{ ...field, width: '100%', fontFamily: 'ui-monospace, monospace' }}
              value={effectiveKey}
              onChange={(e) => setKey(slugify(e.target.value))}
              placeholder="greywater_reuse"
            />
          </div>
          <div>
            <span style={label}>Description (optional)</span>
            <input style={{ ...field, width: '100%' }} value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
        </div>

        {err && <div style={{ marginTop: 10, fontSize: 12.5, color: 'var(--danger, #dc2626)' }}>{err}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button type="button" style={btn} onClick={onClose}>Cancel</button>
          <button type="button" style={btnAccent} onClick={create} disabled={busy || !title.trim() || !effectiveKey}>
            {busy ? <Loader2 size={14} className="spin" /> : null} Create
          </button>
        </div>
      </div>
    </div>
  );
}
